'use client'

import { useMemo, memo } from 'react'
import { CompactTag } from '@/ui/compact-tag'
import { PRIORITY_COLORS } from '@/entries'
import { ENTRY_PRESENTATION } from '@/entries'
import { formatRelativeTime, formatDueDate } from '@/lib/date'
import { cn } from '@/lib/tailwind'
import { isTaskEntry } from '@/entries'
import type { TaskMeta } from '@/entries'
import type { MovieMeta, BookMeta } from '@/entries'
import type { TimelineEntry } from './EntriesTimeline'

export const EntryRow = memo(function EntryRow({
  entry,
}: {
  entry: TimelineEntry
}) {
  const isTask = isTaskEntry(entry.data)
  const taskMeta = isTask ? (entry.data.metadata as TaskMeta) : null
  const isDone = taskMeta?.status === 'done'
  const presentation = ENTRY_PRESENTATION[entry.type]
  const Icon = presentation.icon

  const dueInfo = useMemo(
    () => (taskMeta?.dueDate ? formatDueDate(taskMeta.dueDate) : null),
    [taskMeta],
  )
  const relativeTime = useMemo(
    () => formatRelativeTime(entry.data.createdAt),
    [entry.data],
  )

  // Get display title
  const displayTitle = entry.data.title || 'Untitled'

  // Get subtitle based on entry type
  const subtitle = useMemo(() => {
    const meta = entry.data.metadata
    switch (entry.type) {
      case 'movie': {
        const movieMeta = meta as MovieMeta
        return movieMeta.director
          ? `${movieMeta.director}${movieMeta.year ? ` (${movieMeta.year})` : ''}`
          : movieMeta.year?.toString()
      }
      case 'book': {
        const bookMeta = meta as BookMeta
        const year = (meta as Record<string, unknown>).year
        return bookMeta.author
          ? `${bookMeta.author}${year ? ` (${year})` : ''}`
          : typeof year === 'number'
            ? year.toString()
            : undefined
      }
      case 'release': {
        const year = (meta as Record<string, unknown>).year
        return typeof year === 'number' ? year.toString() : undefined
      }
      default:
        return null
    }
  }, [entry.type, entry.data])

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md px-2 py-1.5',
        isDone && 'opacity-60',
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded',
          presentation.bg,
          presentation.text,
        )}
      >
        <Icon
          className="h-3 w-3"
          weight={isTask && isDone ? 'fill' : 'regular'}
        />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className={cn('truncate text-sm', isDone && 'line-through')}>
          {displayTitle}
        </p>
        <div className="mt-0.5 flex items-center gap-2">
          <span className="text-muted-foreground text-xs">{relativeTime}</span>
          {subtitle && (
            <span className="text-muted-foreground max-w-[150px] truncate text-xs">
              {subtitle}
            </span>
          )}
          {isTask && taskMeta?.priority && taskMeta.priority !== 'low' && (
            <CompactTag
              variant="colored"
              className={
                PRIORITY_COLORS[
                  taskMeta.priority as keyof typeof PRIORITY_COLORS
                ]
              }
            >
              {taskMeta.priority}
            </CompactTag>
          )}
          {dueInfo && (
            <span
              className={cn(
                'text-xs',
                dueInfo.isOverdue
                  ? 'text-destructive'
                  : 'text-muted-foreground',
              )}
            >
              {dueInfo.text}
            </span>
          )}
        </div>
      </div>
    </div>
  )
})
